// ─── Trails Query & Mutation Hooks ───────────────────────────────────
// React Query hooks for trail data.
// These replace the useEffect+useState pattern in TrailsContext.

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { queryKeys } from '@/lib/queryKeys';
import {
  fetchMyTrails,
  fetchSavedTrails,
  fetchMyTrailsStats,
  fetchTrailDetail,
  saveTrailAPI,
  saveFollowTrailAPI,
  deleteTrailAPI,
  updateTrailNameAPI,
  updateTrailPhotoAPI,
  deleteTrailImageAPI,
  updateTrailDetailsAPI,
  saveTrailBookmarkAPI,
  removeTrailBookmarkAPI,
  type TrailPage,
  type TrailStats,
} from '@/lib/api/trails';
import type { Trail } from '@/types/trail';

const dedupeTrailsById = (items: Trail[]): Trail[] => {
  const seen = new Set<string>();
  return items.filter((trail) => {
    const id = String((trail as any)?.id ?? '');
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

// ─── InfiniteData<TrailPage> helpers ───────────────────────────────
// Mutations keep the paginated myTrails/savedTrails caches in sync without a
// full refetch. Prepend/filter/map operate per-page; nextOffset is preserved
// so the cursor stays valid. ponytail: offset pagination + in-place mutation
// can drift by one row on the next page fetch after a delete; acceptable for
// low-frequency writes (own-trail edits). Invalidate if it ever matters.

function prependFirstPage(
  data: InfiniteData<TrailPage> | undefined,
  trail: Trail,
): InfiniteData<TrailPage> | undefined {
  if (!data || data.pages.length === 0) return data;
  const [first, ...rest] = data.pages;
  return {
    ...data,
    pages: [{ trails: dedupeTrailsById([trail, ...first.trails]), nextOffset: first.nextOffset, total: first.total }, ...rest],
  };
}

function filterAllPages(
  data: InfiniteData<TrailPage> | undefined,
  id: string,
): InfiniteData<TrailPage> | undefined {
  if (!data) return data;
  return { ...data, pages: data.pages.map((p) => ({ ...p, trails: p.trails.filter((t) => t.id !== id) })) };
}

/** Flatten an infinite trail query into a plain list (for stat/lookup use). */
export function flattenTrailPages(data: InfiniteData<TrailPage> | undefined): Trail[] {
  return data?.pages.flatMap((p) => p.trails) ?? [];
}

// ─── Queries ─────────────────────────────────────────────────────────

/** Fetch the current user's created trails (paginated). */
export function useMyTrails(token: string | undefined, userId: string | undefined, dogId?: string) {
  return useInfiniteQuery<TrailPage, Error>({
    queryKey: [...queryKeys.trails.myTrails, dogId ?? null],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchMyTrails(token!, userId!, dogId, pageParam as number),
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled: !!token && !!userId,
    staleTime: 2 * 60 * 1000, // 2 min
  });
}

/** Aggregate totals (count / time / distance) over the user's original trails. */
export function useMyTrailsStats(token: string | undefined, dogId?: string) {
  return useQuery<TrailStats, Error>({
    queryKey: [...queryKeys.trails.myTrails, 'stats', dogId ?? null],
    queryFn: () => fetchMyTrailsStats(token!, dogId),
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
  });
}

/** Fetch the current user's saved/bookmarked trails (paginated). */
export function useSavedTrails(token: string | undefined, dogId?: string) {
  return useInfiniteQuery<TrailPage, Error>({
    queryKey: [...queryKeys.trails.savedTrails, dogId ?? null],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchSavedTrails(token!, dogId, pageParam as number),
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
  });
}

/** Fetch a single trail by ID. */
export function useTrailDetail(id: string | undefined, token: string | undefined, dogId?: string) {
  return useQuery({
    queryKey: queryKeys.trails.detail(id ?? '', dogId),
    queryFn: () => fetchTrailDetail(id!, token, dogId),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 min — cache trail details
  });
}

// ─── Cache maintenance ───────────────────────────────────────────────
// Trail mutations update only the trail that changed, in every cached view:
// details live under ['trails','detail', id, dogId] (prefix = all variants),
// paginated lists under ['trails', my|saved|nearby|forYou|tag|search, …],
// group screens under ['groups','trails', …].

function invalidateTrailCaches(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['trails'] });
  queryClient.invalidateQueries({ queryKey: ['groups', 'trails'] });
}

const TRAIL_LIST_KINDS = ['my', 'saved', 'nearby', 'forYou', 'tag', 'search'];

function isTrailListKey(key: unknown): boolean {
  if (!Array.isArray(key)) return false;
  // the Explore screen feeds use ['explore', category, …] keys
  if (key[0] === 'explore') return true;
  return key[0] === 'trails' && TRAIL_LIST_KINDS.includes(key[1] as string);
}

function listContainsTrail(data: unknown, trailId: string): boolean {
  const inf = data as InfiniteData<TrailPage> | undefined;
  return !!inf?.pages?.some((p) => p.trails.some((t) => t.id === (trailId as string)));
}

/** Replace `updated` in every cached list that contains it + refetch that
 *  trail's detail keys (dogId prefix) — the PUT response doesn't carry
 *  trail_points/charts data, so details refetch authoritatively. */
function patchUpdatedTrailEverywhere(queryClient: QueryClient, updated: Trail) {
  if (!updated?.id) return;
  queryClient.invalidateQueries({ queryKey: ['trails', 'detail', updated.id] });
  queryClient.setQueriesData<InfiniteData<TrailPage>>(
    { predicate: (q) => isTrailListKey(q.queryKey) },
    (old) =>
      old && old.pages.some((p) => p.trails.some((t) => t.id === updated.id))
        ? { ...old, pages: old.pages.map((p) => ({ ...p, trails: p.trails.map((t) => (t.id === updated.id ? updated : t)) })) }
        : old,
  );
}

/** Refetch only the lists that still contain the deleted trail + drop its
 *  detail entries. */
function patchDeletedTrailEverywhere(queryClient: QueryClient, trailId: string) {
  queryClient.removeQueries({ queryKey: ['trails', 'detail', trailId] });
  queryClient.setQueriesData<InfiniteData<TrailPage>>(
    { predicate: (q) => isTrailListKey(q.queryKey) && listContainsTrail(q.state.data, trailId) },
    (old) => filterAllPages(old, trailId),
  );
}

/** Image delete has no response body: refetch that trail's details + only the
 *  cached lists that contain it. */
function refreshTrailViewsAfterImageChange(queryClient: QueryClient, trailId: string) {
  queryClient.invalidateQueries({ queryKey: ['trails', 'detail', trailId] });
  queryClient.invalidateQueries({
    predicate: (q) => isTrailListKey(q.queryKey) && listContainsTrail(q.state.data, trailId),
  });
}

/** Visibility change returns only {visibility, sharedGroupId}: patch cached
 *  trail objects in place. Keep the old group name when the shared group
 *  didn't change, clear it otherwise (detail refetch brings the real one). */
export function patchVisibilityEverywhere(
  queryClient: QueryClient,
  trailId: string,
  visibility: string,
  sharedGroupId?: string | null,
) {
  const patchTrail = (t: Trail): Trail => ({
    ...t,
    visibility: visibility as Trail['visibility'],
    sharedGroupId: sharedGroupId ?? null,
    sharedGroupName:
      visibility === 'family' || visibility === 'pack'
        ? (t.sharedGroupId === sharedGroupId ? t.sharedGroupName ?? null : null)
        : null,
  });
  queryClient.setQueriesData<Trail>({ queryKey: ['trails', 'detail', trailId] }, (old) => (old ? patchTrail(old) : old));
  queryClient.setQueriesData<InfiniteData<TrailPage>>(
    { predicate: (q) => isTrailListKey(q.queryKey) && listContainsTrail(q.state.data, trailId) },
    (old) =>
      old ? { ...old, pages: old.pages.map((p) => ({ ...p, trails: p.trails.map((t) => (t.id === trailId ? patchTrail(t) : t)) })) } : old,
  );
}

/** Create flows only: a NEW trail shifts list pagination offsets, so the
 *  explore feeds need a proper refetch (my/saved use the returned object). */
function invalidateExploreFeedCaches(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) &&
      (q.queryKey[0] === 'explore' ||
        (q.queryKey[0] === 'trails' && ['nearby', 'forYou', 'tag', 'search'].includes(q.queryKey[1] as string))),
  });
}

export function useSaveTrail(token: string | undefined, userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { trail: Trail; visibility?: import('@/types/group').Visibility; sharedGroupId?: string | null }) => {
      if (!token || !userId) throw new Error('Not authenticated');
      return saveTrailAPI(token, userId, args.trail, { visibility: args.visibility, sharedGroupId: args.sharedGroupId });
    },
    onSuccess: () => {
      // create: rare + pagination offsets shift — full invalidate is correct here
      invalidateTrailCaches(queryClient);
    },
  });
}

/** Save a followed trail. */
export function useSaveFollowTrail(token: string | undefined, userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceTrailId, trail, review, photos }: {
      sourceTrailId: string;
      trail: Trail;
      review: { rating: number; content?: string };
      photos?: string[];
    }) => {
      if (!token || !userId) throw new Error('Not authenticated');
      return saveFollowTrailAPI(token, userId, sourceTrailId, trail, review, photos);
    },
    onSuccess: () => {
      // create: rare + pagination offsets shift — full invalidate is correct here
      invalidateTrailCaches(queryClient);
    },
  });
}

/** Delete a trail. */
export function useDeleteTrail(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (!token) throw new Error('Not authenticated');
      return deleteTrailAPI(token, id);
    },
    onSuccess: (_data, id) => {
      // surgical: drop the deleted trail from cached lists + its detail keys
      patchDeletedTrailEverywhere(queryClient, id);
      queryClient.invalidateQueries({ queryKey: ['groups', 'trails'] });
    },
  });
}

/** Update a trail's name. */
export function useUpdateTrailName(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => {
      if (!token) throw new Error('Not authenticated');
      return updateTrailNameAPI(token, id, name);
    },
    onSuccess: (updated) => {
      patchUpdatedTrailEverywhere(queryClient, updated);
    },
  });
}

/** Update trail photos. */
export function useUpdateTrailPhoto(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, photoOrUrls }: { id: string; photoOrUrls: string | string[] }) => {
      if (!token) throw new Error('Not authenticated');
      return updateTrailPhotoAPI(token, id, photoOrUrls);
    },
    onSuccess: (updated) => {
      patchUpdatedTrailEverywhere(queryClient, updated);
    },
  });
}

/** Delete a trail image. */
export function useDeleteTrailImage(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trailId, imageId }: { trailId: string; imageId: string }) => {
      if (!token) throw new Error('Not authenticated');
      return deleteTrailImageAPI(token, trailId, imageId);
    },
    onSuccess: (_data, { trailId }) => {
      refreshTrailViewsAfterImageChange(queryClient, trailId);
    },
  });
}

/** Update trail details (rating, etc). */
export function useUpdateTrailDetails(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof updateTrailDetailsAPI>[2] }) => {
      if (!token) throw new Error('Not authenticated');
      return updateTrailDetailsAPI(token, id, updates);
    },
    onSuccess: (updated) => {
      patchUpdatedTrailEverywhere(queryClient, updated);
    },
  });
}

/** Bookmark (save) a trail. */
export function useSaveTrailBookmark(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (trailId: string) => {
      if (!token) throw new Error('Not authenticated');
      return saveTrailBookmarkAPI(token, trailId);
    },
    onSuccess: (saved) => {
      // Prepend to the paginated savedTrails cache (prefix match covers any dogId variant).
      if (saved?.id) {
        queryClient.setQueriesData<InfiniteData<TrailPage>>({ queryKey: queryKeys.trails.savedTrails }, (old) => prependFirstPage(old, saved));
      }
    },
  });
}

/** Remove a trail bookmark. */
export function useRemoveTrailBookmark(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (trailId: string) => {
      if (!token) throw new Error('Not authenticated');
      return removeTrailBookmarkAPI(token, trailId);
    },
    onSuccess: (_data, trailId) => {
      queryClient.setQueriesData<InfiniteData<TrailPage>>({ queryKey: queryKeys.trails.savedTrails }, (old) => filterAllPages(old, trailId));
    },
  });
}

/** Check whether a trail is in the user's saved/bookmarked list.
 *  Reads from the savedTrails query cache for instant checks. */
export function useIsTrailSaved(token: string | undefined) {
  const savedQuery = useSavedTrails(token);
  const savedTrails = useMemo(() => flattenTrailPages(savedQuery.data), [savedQuery.data]);
  const isTrailSaved = useCallback(
    (trailId: string) => savedTrails.some((t) => t.id === trailId),
    [savedTrails],
  );
  return isTrailSaved;
}